import 'swiper/css'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'
import { reactMount } from '@/lib/reactMount'
import { useRef, useState, useEffect, useImperativeHandle } from 'react'
import { ArrowRight } from '@/components/Icons'
import { SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/react/24/solid'
import { Button } from '@/components/Button'
import { rteBlock as RteBlock } from './rteBlock'
import { loanCalculatorBlock as CalculatorBlock } from './loanCalculator'
import { getImageByBreakpoint } from '@/lib/media'
import { gtmTrackEvent } from '@/lib/analytics'
import { getHeadingClasses } from '@/lib/headings'

// Hook to detect if we're on mobile device (< 768px)
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(true)

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    // Check on mount
    checkIsMobile()

    // Listen for resize events
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  return isMobile
}

const BackgroundMedia = ({ media, onVideoEnd, isActive, isMobile, controlRef, isMuted }) => {
  const videoRef = useRef(null)
  const imageByBreakpoint = getImageByBreakpoint(media.Image)

  useImperativeHandle(controlRef, () => ({
    setMuted (muted) {
      const video = videoRef.current
      if (!video) return
      video.muted = muted
    }
  }), [])

  useEffect(() => {
    const video = videoRef.current
    if (!video || isMobile) return // Don't handle video on mobile

    if (isActive) {
      // Keep muted on activation so autoplay is allowed by the browser
      video.muted = true
      video.currentTime = 0
      video.play().catch(err => console.log('Video play failed:', err))
    } else {
      video.pause()
      video.muted = true
    }
  }, [isActive, isMobile])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !isActive || isMobile) return // Don't handle video on mobile

    const handleEnded = () => {
      onVideoEnd?.()
    }

    const handlePlay = () => {
      // Pause autoplay when video starts playing
      onVideoEnd?.('pause')
    }

    video.addEventListener('ended', handleEnded)
    video.addEventListener('play', handlePlay)

    return () => {
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('play', handlePlay)
    }
  }, [isActive, onVideoEnd, isMobile])

  // On mobile, always show image (even if video exists)
  // On tablet/desktop, show video if available, otherwise show image
  if (isMobile) {
    // Mobile: always show picture element
    if (media?.Image?.Url) {
      return (
        <picture>
          {Object.keys(imageByBreakpoint).reverse().map((minWidth) => (
            <source key={minWidth} media={`(min-width: ${minWidth})`} srcSet={imageByBreakpoint[minWidth]} />
          ))}
          <img
            src={media.Image.Url}
            alt={media.Image.AltText || ''}
            className='w-full h-full object-cover'
          />
        </picture>
      )
    }
    return null
  } else {
    // Tablet/Desktop: show video if available
    if (media?.VideoUrl) {
      return (
        <video
          ref={videoRef}
          src={media.VideoUrl}
          className='w-full h-full object-cover'
          loop={false}
          muted={isMuted}
          playsInline
        />
      )
    } else if (media?.Image?.Url) {
      return (
        <picture>
          {Object.keys(imageByBreakpoint).reverse().map((minWidth) => (
            <source key={minWidth} media={`(min-width: ${minWidth})`} srcSet={imageByBreakpoint[minWidth]} />
          ))}
          <img
            src={media.Image.Url}
            alt={media.Image.AltText || ''}
            className='w-full h-full object-cover'
          />
        </picture>
      )
    }
    return null
  }
}

const SlideContent = ({ slide, alignment, currentSlide }) => {
  if (!slide) return null

  const headingClasses = getHeadingClasses(slide.TitleHeadingSize, { fallback: 'medium' })

  // Button logic matching standaloneCtaBlock pattern
  const primary = slide.Button
  const secondary = slide.SecondaryButton
  const tertiary = slide.TertiaryButton
  const hyperlink = slide.ButtonText

  const buttons = []

  // Primary
  if (primary) {
    buttons.push({
      ...primary,
      variant: 'primary',
      size: 'lg'
    })
  }

  // Secondary preferred over tertiary
  if (secondary) {
    buttons.push({
      ...secondary,
      variant: 'secondary',
      size: 'lg'
    })
  } else if (tertiary) {
    buttons.push({
      ...tertiary,
      variant: 'primary-alt',
      size: 'lg'
    })
  }
  // Hyperlink only if nothing else exists
  else if (hyperlink) {
    buttons.push({
      ...hyperlink,
      variant: 'link',
      size: 'lg'
    })
  }

  return (
    <div>
      <div className='line-clamp-6'>
        <h2 className={`${headingClasses} font-extrabold mb-2 tracking-tight pb-[11px] text-pretty`}>{slide.Title}</h2>
        <p className='text-xl sm:text-[22px] font-normal mb-3'>{slide.SubTitle}</p>
        {/* Summary is HTML */}
        <div className='text-base font-semibold mb-4' dangerouslySetInnerHTML={{ __html: slide.Summary || '' }} />
      </div>
      <div className={`flex md:flex-row items-center flex-wrap gap-y-2.5 gap-x-6 pointer-events-auto pt-3 lg:pt-5 ${alignment === 'center' ? 'justify-center' : ''}`}>
        {buttons.map((btn, index) => (
          <Button
            key={index}
            href={btn.Url}
            target={btn.Target}
            variant={btn.variant}
            size={btn.size}
            componentName={btn.Url?.includes('#apply-dialog') ? 'Hero Carousel Block' : null}
            onClick={() => {
              if (!btn.Url?.includes('#apply-dialog')) {
                gtmTrackEvent('clickButtonHeroCarousel', {
                  buttonType: btn.variant,
                  buttonName: btn.Name,
                  link: btn.Url,
                  slideTitle: slide.Title,
                  slideNumber: currentSlide + 1,
                  componentName: 'Hero Carousel Block',
                  pageURL: window.location.href
                })
              }
            }}
          >
            {btn.variant === 'primary-alt'
              ? (
                <span className='block relative pr-7'>{btn.Name} <ArrowRight className='absolute -right-0.5 top-1/2 -mt-[11px]' /></span>
                )
              : (
                  btn.Name
                )}
          </Button>
        ))}
      </div>
    </div>
  )
}

const navButtonClass = 'inline-flex items-center justify-center leading-none text-primary-50 border border-primary-50 px-5 py-[7px] rounded-full transition-all pointer-events-auto shadow-sm hover:bg-default-100 hover:text-default-900 duration-300'

const NavigationControls = ({ goPrev, goNext, currentSlide, slidesLength, alignment, showMuteToggle, isMuted, onToggleMute }) => (
  <div className='flex items-end pt-4 lg:pt-10'>
    <div className={`flex w-full items-center gap-4 ${alignment === 'center' ? 'w-max mx-auto' : ''}`}>
      {showMuteToggle && (
        <button
          type='button'
          onClick={onToggleMute}
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          aria-pressed={!isMuted}
          className={`${navButtonClass} ${alignment === 'center' ? 'order-1' : 'order-2'}`}
        >
          {isMuted
            ? <SpeakerXMarkIcon className='h-[25px] w-[25px]' aria-hidden='true' />
            : <SpeakerWaveIcon className='h-[25px] w-[25px]' aria-hidden='true' />}
        </button>
      )}
      <button
        onClick={goPrev}
        className={`${navButtonClass} ${alignment === 'center' ? 'order-1' : 'order-2'}`}
      >
        <ArrowRight className='transform rotate-180' />
        <span className='sr-only'>Prev</span>
      </button>
      <span className={`text-primary-50 text-sm px-3 py-1 rounded pointer-events-auto w-16 grow ${alignment === 'center' ? 'order-2 text-center' : 'order-1'}`}>
        {currentSlide + 1} of {slidesLength}
      </span>
      <button
        onClick={goNext}
        className={`${navButtonClass} order-3`}
      >
        <ArrowRight />
        <span className='sr-only'>Next</span>
      </button>
    </div>
  </div>
)

const Overlay = () => (
  <div className='absolute inset-0 z-20 pointer-events-none'>
    <div className='_hero-g-l-r absolute inset-0 opacity-60' />
    <div className='_hero-g-b-t absolute inset-0' />
  </div>
)

export const heroCarouselBlock = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current
  const theme = umbraco.Theme || 'dark'
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isFading, setIsFading] = useState(false)
  const [autoplayPaused, setAutoplayPaused] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const swiperRef = useRef(null)
  const activeVideoApi = useRef(null)
  const isMobile = useIsMobile()

  const slides = umbraco.Carousel || []
  const currentSlideHasVideo = !isMobile && Boolean(slides[currentSlide]?.Media?.VideoUrl)

  const toggleMute = (event) => {
    event.stopPropagation()
    const nextMuted = !isMuted
    // Set muted in the click handler so the browser treats it as a user gesture
    activeVideoApi.current?.setMuted(nextMuted)
    setIsMuted(nextMuted)
  }

  const handleSlideChange = (swiper) => {
    setCurrentSlide(swiper.realIndex)
    setIsMuted(true)
    // Resume autoplay when changing slides (in case it was paused by video)
    if (autoplayPaused && swiperRef.current?.swiper?.autoplay) {
      swiperRef.current.swiper.autoplay.start()
      setAutoplayPaused(false)
    }
  }

  const handleVideoEvent = (action) => {
    if (!swiperRef.current?.swiper?.autoplay) return
    if (action === 'pause') {
      // Pause autoplay when video starts playing
      swiperRef.current.swiper.autoplay.stop()
      setAutoplayPaused(true)
    } else {
      // Video ended - fade to next slide and resume autoplay
      setAutoplayPaused(false)
      handleFadeAndSlide(() => {
        swiperRef.current.swiper.slideNext()
      })
      // Resume autoplay after fade completes
      setTimeout(() => {
        if (swiperRef.current?.swiper?.autoplay) {
          swiperRef.current.swiper.autoplay.start()
        }
      }, FADE_DURATION + 50)
    }
  }

  const FADE_DURATION = 300 // ms, match with CSS

  const handleFadeAndSlide = (slideFn) => {
    setIsFading(true)
    setTimeout(() => {
      slideFn()
      setTimeout(() => setIsFading(false), 10) // allow React to re-render
    }, FADE_DURATION)
  }

  const goNext = () => {
    handleFadeAndSlide(() => {
      if (swiperRef.current?.swiper) {
        swiperRef.current.swiper.slideNext()
      }
    })
  }

  const goPrev = () => {
    handleFadeAndSlide(() => {
      if (swiperRef.current?.swiper) {
        swiperRef.current.swiper.slidePrev()
      }
    })
  }

  const alignment = umbraco.Alignment || 'left'
  const blocks = umbraco.Blocks || []
  const bottomContentImageCta = umbraco.BottomContentImageCta || null

  return (
    <div data-theme={theme}>
      <div className='relative bg-primary-950'>
        {/* Background Swiper */}
        <Swiper
          ref={swiperRef}
          spaceBetween={0}
          slidesPerView={1}
          loop={slides.length >= 3}
          pagination={{ clickable: true }}
          scrollbar={{ draggable: true }}
          autoplay={{
            delay: 6000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true
          }}
          modules={[Autoplay]}
          onSlideChange={handleSlideChange}
          className='h-[75vh] md:h-[80vh] min-h-[640px] md:min-h-[740px] xl:min-h-[760px]'
        >
          {slides.map((slide, idx) => (
            <SwiperSlide key={idx}>
              <div className='absolute inset-0'>
                <BackgroundMedia
                    media={slide.Media}
                    onVideoEnd={handleVideoEvent}
                    isActive={idx === currentSlide}
                    isMobile={isMobile}
                    controlRef={idx === currentSlide ? activeVideoApi : null}
                    isMuted={idx === currentSlide ? isMuted : true}
                  />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Content overlay positioned on top of Swiper */}
        <div className='absolute inset-0 z-30 flex items-end pointer-events-none'>
          <div className='mx-auto w-full max-w-[1440px] h-full flex items-end px-4 md:px-6 lg:px-8'>
            <div className={`text-white w-full pointer-events-none flex flex-col justify-end h-full ${(blocks.length === 0 || !bottomContentImageCta) ? 'pb-8 md:pb-16 xl:pb-[150px]' : 'pb-10 xl:pb-[150px]'} ${alignment === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-[480px]'} `}>
              <div className={`transition-opacity duration-300 ${isFading ? 'opacity-0' : 'opacity-100'}`}>
                <SlideContent slide={slides[currentSlide]} alignment={alignment} currentSlide={currentSlide} />
              </div>

              {slides.length > 1 && (
                <NavigationControls
                    goPrev={goPrev}
                    goNext={goNext}
                    currentSlide={currentSlide}
                    slidesLength={slides.length}
                    alignment={alignment}
                    showMuteToggle={currentSlideHasVideo}
                    isMuted={isMuted}
                    onToggleMute={toggleMute}
                  />
              )}
            </div>
          </div>
        </div>

        <Overlay />
      </div>

      <div className='bg-primary-950'>
        {umbraco.BottomContentImage && (
          <div className='relative z-30 px-4 max-w-[722px] mx-auto last:pb-8 -mt-6 xl:-mt-28'>

            {umbraco.BottomContentImageCta?.Url
              ? (
                <a href={umbraco.BottomContentImageCta.Url} target={umbraco.BottomContentImageCta.Target || '_self'} className='relative w-full aspect-contentHeroBottom block' title={umbraco.BottomContentImage?.AltText || ''}>
                  <picture>
                    <source srcSet={umbraco.BottomContentImage?.Url640} media='(min-width: 640px)' />
                    <img src={umbraco.BottomContentImage?.Url} alt={umbraco.BottomContentImage?.AltText || ''} className='absolute inset-0 w-full h-full object-cover' />
                  </picture>
                </a>
                )
              : (
                <div className='relative w-full aspect-contentHeroBottom'>
                  <img src={umbraco.BottomContentImage?.Url} alt={umbraco.BottomContentImage?.AltText || ''} className='absolute inset-0 w-full h-full object-cover' />
                </div>
                )}
          </div>
        )}
        {umbraco.Blocks?.map((block, index) => {
          if (block.BlockType === 'rteBlock') {
            return <RteBlock version='hero-carousel' key={'RteBlock-' + index} umbraco={block} />
          }
          if (block.BlockType === 'calculatorBlock') {
            return <CalculatorBlock version='hero-carousel' key={'CalculatorBlock-' + index} umbraco={block} />
          }
          return null
        })}

      </div>
    </div>
  )
}

reactMount('heroCarouselBlock', heroCarouselBlock)
